import React, { useState, useEffect, useRef } from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import ClipLoader from 'react-spinners/ClipLoader';
import { toast } from 'react-toastify';

import styles from '../../../styles/AddressSearch.module.css';
import poweredByGoogleLogo from '../../../../SmartSend/assets/powered_by_google_on_white.png';
import { getGoogleApiKey } from '../../../env/config';

/**
 * AddressSearchForm Component - Google Places Autocomplete Form
 *
 * Used in SmartSend v2 for address selection with automatic timezone detection
 * Follows v2 design patterns with CSS variables and modern styling
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the form is visible
 * @param {Function} props.onClose - Callback to close/cancel the form
 * @param {Function} props.onAddressSelect - Callback when address is saved
 * @param {Object} props.existingAddress - Pre-filled address data (optional)
 * @param {boolean} props.isValidating - Whether zoning validation is in progress
 */
const AddressSearchForm = ({
  isOpen,
  onClose,
  onAddressSelect,
  existingAddress = null,
  isValidating = false,
}) => {
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [locality, setLocality] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [postcode, setPostcode] = useState('');
  const [latLng, setLatLng] = useState(null);
  const [timezone, setTimezone] = useState(null);

  // Ref to preserve address data during validation to prevent iOS form reset
  const addressDataRef = useRef(null);

  // Synchronous flag to track validation state immediately (before React re-renders)
  // This fixes the race condition where isValidating prop isn't true until next render
  const isValidatingRef = useRef(false);

  // Ref to the form card for checking if focused elements are within this form
  const formCardRef = useRef(null);

  /**
   * Helper function to clear all address form fields
   * Centralizes field clearing logic to ensure consistency
   */
  const clearAddressFields = () => {
    setAddress1('');
    setAddress2('');
    setLocality('');
    setState('');
    setCountry('');
    setPostcode('');
    setLatLng(null);
    setTimezone(null);
  };

  // Pre-fill fields if editing existing address, or reset if no address
  // CRITICAL: Don't reset form fields while validation is in progress
  // This prevents iOS from clearing inputs when "Save Address" button is clicked
  useEffect(() => {
    // PRIORITY 0: Check synchronous validation flag first (fixes race condition)
    // This catches the case where button was clicked but isValidating prop hasn't updated yet
    if (isValidatingRef.current) {
      return;
    }

    // PRIORITY 1: If we have preserved address data in ref, use it
    // This handles the case where validation is starting but isValidating isn't true yet
    // or where existingAddress becomes null during validation
    if (addressDataRef.current) {
      const preserved = addressDataRef.current;
      setAddress1(preserved.address1 || '');
      setAddress2(preserved.address2 || '');
      setLocality(preserved.locality || '');
      setState(preserved.state || '');
      setCountry(preserved.country || '');
      setPostcode(preserved.postcode || '');
      setLatLng(
        preserved.lat && preserved.lng
          ? { lat: preserved.lat, lng: preserved.lng }
          : null
      );
      setTimezone(preserved.timezone || null);

      // Clear ref if validation completed successfully (existingAddress is set)
      // If validation failed, keep the ref so form shows user's input
      if (existingAddress && !isValidating) {
        addressDataRef.current = null;
      }

      // Don't proceed with normal logic if we're preserving data
      return;
    }

    // PRIORITY 2: If validation is in progress (prop-based check), preserve current state
    // Don't reset fields during validation
    if (isValidating) {
      // Don't proceed with normal reset logic during validation
      return;
    }

    // PRIORITY 3: Normal form initialization/reset logic (only when not validating and no preserved data)
    if (existingAddress) {
      setAddress1(existingAddress.address1 || '');
      setAddress2(existingAddress.address2 || '');
      setLocality(existingAddress.locality || '');
      setState(existingAddress.state || '');
      setCountry(existingAddress.country || '');
      setPostcode(existingAddress.postcode || '');
      setLatLng(
        existingAddress.lat && existingAddress.lng
          ? {
              lat: existingAddress.lat,
              lng: existingAddress.lng,
            }
          : null
      );
      setTimezone(existingAddress.timezone || null);
    } else {
      // Reset all fields when starting fresh (only when not validating and no preserved data)
      setAddress1('');
      setAddress2('');
      setLocality('');
      setState('');
      setCountry('');
      setPostcode('');
      setLatLng(null);
      setTimezone(null);
    }
  }, [existingAddress, isOpen, isValidating]);

  // Sync isValidatingRef with isValidating prop (clear ref when validation completes)
  useEffect(() => {
    if (!isValidating) {
      isValidatingRef.current = false;
    }
  }, [isValidating]);

  /**
   * Ensures form remains scrollable when keyboard is open on mobile
   * Scrolls focused inputs into view and ensures the Save button remains accessible
   * Uses visualViewport API to account for keyboard height on mobile devices
   */
  useEffect(() => {
    if (!isOpen) return;

    // Use attribute selector to find contentWrapper (more robust than class name)
    // The contentWrapper is in the parent SmartSendFlow component
    const scrollContainer = document.querySelector('[class*="contentWrapper"]');
    if (!scrollContainer) return;

    // Constants for scroll calculations
    const KEYBOARD_PADDING = 200; // Padding from bottom to account for keyboard
    const TOP_PADDING = 50; // Minimum padding from top
    const KEYBOARD_ANIMATION_DELAYS = [100, 300, 500]; // Delays for different device animations
    const BUTTON_SCROLL_DELAY = 600; // Delay before checking button accessibility

    const scrollElementIntoView = (element) => {
      if (!element) return;

      // Get viewport height accounting for keyboard
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const elementRect = element.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Calculate visible area (accounting for keyboard)
      const visibleBottom = Math.min(containerRect.bottom, viewportHeight);
      const elementBottom = elementRect.bottom;
      const elementTop = elementRect.top;

      // Only scroll if element is actually hidden or would be hidden by keyboard
      // Check if element is below visible area (would be hidden by keyboard)
      const isBelowVisible = elementBottom > visibleBottom - KEYBOARD_PADDING;
      // Check if element is above visible area (would be hidden at top)
      const isAboveVisible = elementTop < TOP_PADDING;

      // Only scroll if element is hidden or would be hidden
      // Using 'nearest' instead of 'center' to minimize scrolling
      // This will only scroll the minimum amount needed to make element visible
      if (isBelowVisible || isAboveVisible) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    };

    const handleFocus = (e) => {
      const target = e.target;
      // Only handle input, textarea, and select elements within this form
      if (!target.matches('input, textarea, select')) return;

      // Ensure the target is within our form card
      if (!formCardRef.current || !formCardRef.current.contains(target)) {
        return;
      }

      // Wait for keyboard to appear and DOM to update
      // Use multiple timeouts to handle keyboard animation on different devices
      requestAnimationFrame(() => {
        KEYBOARD_ANIMATION_DELAYS.forEach((delay) => {
          setTimeout(() => scrollElementIntoView(target), delay);
        });

        // Also ensure button is accessible after scrolling
        setTimeout(() => {
          const button = formCardRef.current?.querySelector(`button.${styles.saveButton}`);
          if (button) {
            scrollElementIntoView(button);
          }
        }, BUTTON_SCROLL_DELAY);
      });
    };

    // Handle visual viewport resize (keyboard open/close)
    const handleViewportResize = () => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        activeElement.matches('input, textarea, select') &&
        formCardRef.current?.contains(activeElement)
      ) {
        // Re-scroll active input when viewport changes
        setTimeout(() => {
          scrollElementIntoView(activeElement);
        }, 100);
      }
    };

    // Add event listeners
    document.addEventListener('focusin', handleFocus);

    // Listen for visual viewport changes (keyboard open/close)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);
    }

    return () => {
      document.removeEventListener('focusin', handleFocus);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      }
    };
  }, [isOpen]);

  /**
   * Prevent form submissions at document level (safety net for WebView)
   * Catches any form submissions that might be triggered by keyboard "Done" button
   * or other native WebView behaviors that bypass component-level handlers
   * Note: Since we're using div instead of form, this is primarily a safety net
   */
  useEffect(() => {
    const handleDocumentSubmit = (e) => {
      // Only prevent if the submit is from our address form container
      const formCard = document.querySelector(`[data-address-form="true"]`);
      if (formCard && formCard.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Use capture phase to catch events before they bubble
    document.addEventListener('submit', handleDocumentSubmit, true);

    return () => {
      document.removeEventListener('submit', handleDocumentSubmit, true);
    };
  }, []);

  /**
   * Fetches timezone for given coordinates using Google Timezone API
   */
  const fetchTimezone = async (lat, lng) => {
    try {
      const timestamp = Math.floor(Date.now() / 1000);
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${getGoogleApiKey()}`
      );
      const data = await response.json();

      if (data.status === 'OK' && data.timeZoneId) {
        return data.timeZoneId;
      }
      console.error('Timezone fetch error:', data);
      return null;
    } catch (error) {
      console.error('Error fetching timezone:', error);
      return null;
    }
  };

  /**
   * Handles place selection from Google Places Autocomplete
   */
  const handleSelect = async (selectedAddress) => {
    try {
      const results = await geocodeByAddress(selectedAddress);
      const coordinates = await getLatLng(results[0]);

      // Extract address components
      const addressComponents = results[0].address_components;
      let streetNumber = '';
      let route = '';
      let city = '';
      let stateCode = '';
      let countryCode = '';
      let postalCode = '';
      addressComponents.forEach((component) => {
        const types = component.types;

        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          route = component.long_name;
        }
        if (types.includes('locality') || types.includes('sublocality')) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          stateCode = component.short_name;
        }
        if (types.includes('country')) {
          countryCode = component.short_name;
        }
        if (types.includes('postal_code')) {
          postalCode = component.long_name;
        }
      });

      // Construct full street address from components
      const fullAddress = `${streetNumber} ${route}`.trim();

      // Validate that all required address components are present
      // Required: street address (streetNumber or route), city, state, country, postal code
      // If incomplete, show error immediately and don't proceed
      const hasStreetAddress = streetNumber || route;
      if (
        !hasStreetAddress ||
        !city ||
        !stateCode ||
        !countryCode ||
        !postalCode
      ) {
        toast.error(
          'The selected address is missing required information. Please select a different address from the suggestions.',
          { autoClose: 5000 }
        );
        // Clear any partial state to prevent showing incomplete data
        clearAddressFields();
        return;
      }

      // Fetch timezone
      const tz = await fetchTimezone(coordinates.lat, coordinates.lng);

      // If timezone fetch failed, clear the fields to prevent partial state
      if (!tz) {
        toast.error(
          'Unable to determine timezone for this address. Please select a different address.',
          { autoClose: 5000 }
        );
        clearAddressFields();
        return;
      }

      // All validations passed - update state with complete address
      setAddress1(fullAddress);
      setLocality(city);
      setState(stateCode);
      setCountry(countryCode);
      setPostcode(postalCode);
      setLatLng(coordinates);
      setTimezone(tz);
    } catch (error) {
      console.error('Error selecting place:', error);
      toast.error('Failed to process selected address. Please try again.');
      // Clear any partial state that may have been set before the error
      clearAddressFields();
    }
  };

  /**
   * Clears dependent fields when user types in main address field
   */
  const handleAddressChange = (value) => {
    setAddress1(value);

    // Clear other fields if user is changing the main address
    if (locality || state || country || postcode) {
      setLocality('');
      setState('');
      setCountry('');
      setPostcode('');
      setLatLng(null);
      setTimezone(null);
    }
  };

  /**
   * Validates and saves the address
   * @param {Event} e - Click event (optional, for preventing default behavior)
   */
  const handleSave = (e) => {
    // Prevent any form submission or page reload behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Validation
    if (!address1 || !locality || !state || !country || !postcode) {
      toast.error(
        'Please reenter a complete address with all required fields.'
      );
      return;
    }

    if (!latLng) {
      toast.error('Please select a valid address from the suggestions.');
      return;
    }

    if (!timezone) {
      toast.error(
        'Unable to determine timezone for this address. Please try selecting again.'
      );
      return;
    }

    // Construct address object
    const addressData = {
      address1: address1.trim(),
      address2: address2.trim(),
      locality: locality.trim(),
      state: state.trim(),
      postcode: postcode.trim(),
      country: country.trim(),
      lat: latLng.lat,
      lng: latLng.lng,
      timezone,
    };

    // CRITICAL: Set synchronous flags BEFORE calling onAddressSelect
    // This ensures the form state is preserved before isValidating prop becomes true
    // (which happens on the next React render cycle, creating a race condition)
    // This prevents iOS form reset during the validation process
    isValidatingRef.current = true;
    addressDataRef.current = addressData;

    // Call parent callback - this will trigger zoning validation
    // Both refs are already set, so useEffect will preserve form state
    onAddressSelect(addressData);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={formCardRef}
      className={styles.formCard}
      role="form"
      aria-labelledby="address-form-title"
      aria-label="Delivery address form"
      data-address-form="true"
    >
      <div className={styles.header}>
        <h2 className={styles.title} id="address-form-title">
          Delivery Address
        </h2>
      </div>

      <div
        className={styles.formContainer}
        onKeyDown={(e) => {
          // Prevent Enter key from triggering form submission in webview
          if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <PlacesAutocomplete
          value={address1}
          onChange={handleAddressChange}
          onSelect={handleSelect}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => (
            <div className={styles.autocompleteContainer}>
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="address1">
                  Street Address<span className={styles.required}>*</span>
                </label>
                <input
                  {...getInputProps({
                    id: 'address1',
                    placeholder: 'Search for your address...',
                    className: styles.input,
                    'aria-label': 'Search for your street address',
                    'aria-required': 'true',
                    'aria-describedby': 'address1-help',
                    'aria-autocomplete': 'list',
                    'aria-controls':
                      suggestions.length > 0
                        ? 'address-suggestions'
                        : undefined,
                    'aria-expanded': suggestions.length > 0,
                    autoComplete: 'off',
                    form: '',
                  })}
                />
                <span id="address1-help" className="sr-only">
                  Type to search for your address using Google Places
                </span>
              </div>

              {(suggestions.length > 0 || loading) && (
                <div
                  className={styles.dropdownContainer}
                  id="address-suggestions"
                  role="listbox"
                  aria-label="Address suggestions"
                >
                  {loading && (
                    <div
                      className={styles.loadingContainer}
                      role="status"
                      aria-live="polite"
                    >
                      <ClipLoader
                        color="#1a94e6"
                        size={30}
                        aria-hidden="true"
                      />
                      <span className="sr-only">
                        Loading address suggestions
                      </span>
                    </div>
                  )}

                  {suggestions.map((suggestion) => {
                    const className = suggestion.active
                      ? `${styles.suggestionItem} ${styles.suggestionItemActive}`
                      : styles.suggestionItem;

                    return (
                      <div
                        key={suggestion.placeId}
                        {...getSuggestionItemProps(suggestion, {
                          className,
                          role: 'option',
                          'aria-selected': suggestion.active,
                        })}
                      >
                        {suggestion.description}
                      </div>
                    );
                  })}

                  <div className={styles.poweredByGoogle}>
                    <img src={poweredByGoogleLogo} alt="Powered by Google" />
                  </div>
                </div>
              )}
            </div>
          )}
        </PlacesAutocomplete>

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="address2">
            Apartment, Suite, Unit, etc. (Optional)
          </label>
          <input
            id="address2"
            type="text"
            className={styles.input}
            value={address2}
            onChange={(e) => setAddress2(e.target.value)}
            placeholder="Apt 123"
            maxLength={100}
            aria-label="Apartment, suite, or unit number (optional)"
            autoComplete="off"
            form=""
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="locality">
            City<span className={styles.required}>*</span>
          </label>
          <input
            id="locality"
            type="text"
            className={styles.input}
            value={locality}
            disabled
            placeholder="Auto-filled from address"
            aria-label="City (automatically filled)"
            aria-required="true"
            aria-readonly="true"
            autoComplete="off"
            form=""
          />
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.fieldHalf}>
            <label className={styles.label} htmlFor="state">
              State<span className={styles.required}>*</span>
            </label>
            <input
              id="state"
              type="text"
              className={styles.input}
              value={state}
              disabled
              placeholder="Auto-filled"
              aria-label="State (automatically filled)"
              aria-required="true"
              aria-readonly="true"
              autoComplete="off"
              form=""
            />
          </div>

          <div className={styles.fieldHalf}>
            <label className={styles.label} htmlFor="postcode">
              Postal Code<span className={styles.required}>*</span>
            </label>
            <input
              id="postcode"
              type="text"
              className={styles.input}
              value={postcode}
              disabled
              placeholder="Auto-filled"
              aria-label="Postal code (automatically filled)"
              aria-required="true"
              aria-readonly="true"
              autoComplete="off"
              form=""
            />
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="country">
            Country<span className={styles.required}>*</span>
          </label>
          <input
            id="country"
            type="text"
            className={styles.input}
            value={country}
            disabled
            placeholder="Auto-filled from address"
            aria-label="Country (automatically filled)"
            aria-required="true"
            aria-readonly="true"
            autoComplete="off"
            form=""
          />
        </div>

        <button
          type="button"
          className={styles.saveButton}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSave(e);
          }}
          disabled={
            isValidating ||
            !address1 ||
            !locality ||
            !state ||
            !country ||
            !postcode
          }
          aria-label={
            isValidating ? 'Validating address' : 'Save delivery address'
          }
          aria-busy={isValidating}
        >
          {isValidating ? (
            <>
              <ClipLoader color="#ffffff" size={20} aria-hidden="true" />
              <span className="sr-only">Validating address</span>
            </>
          ) : (
            'Save Address'
          )}
        </button>
      </div>
    </div>
  );
};

export default AddressSearchForm;
